import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  fetchLessonById,
  updateLesson,
  clearSelectedEntity,
  clearFormErrors,
  resetSubmissionStatus,
  updateFormData,
  selectSelectedLesson,
  selectInitialFormData,
  selectLessonDetailStatus,
  selectLessonUpdateStatus,
  selectLessonDetailError,
  selectLessonUpdateError,
  selectLessonFormErrors,
} from '../../features/lessons/lessonSlice';
import { selectUserInformations } from '../../features/authSlice';
import SharedForm from '../../components/forms/SharedForm';
import lessonFields from '../../config/lessonFields';
import { showToast } from '../../utils/utils';
import Loader from '../../components/Live/Loader';

/**
 * Edit Lesson Page
 * Full page component for editing an existing lesson
 */
const EditLessonPage = () => {
  const { id, courseId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUserInformations);
  const location = useLocation();
  const course = location.state?.course;

  const selectedLesson = useSelector(selectSelectedLesson);
  const initialFormData = useSelector(selectInitialFormData);
  const detailStatus = useSelector(selectLessonDetailStatus);
  const updateStatus = useSelector(selectLessonUpdateStatus);
  const detailError = useSelector(selectLessonDetailError);
  const updateError = useSelector(selectLessonUpdateError);
  const formErrors = useSelector(selectLessonFormErrors);

  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (id && user?.token) {
      dispatch(fetchLessonById({ token: user.token, lessonId: id }));
    }

    return () => {
      dispatch(clearSelectedEntity());
    };
  }, [id, user?.token, dispatch]);

  useEffect(() => {
    if (selectedLesson) {
      // Include course object from location state if not in selectedLesson
      setFormData({
        ...selectedLesson,
        course: selectedLesson.course || course || null,
      });
    }
  }, [selectedLesson, course]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    dispatch(updateFormData({ [name]: value }));

    if (formErrors[name]) {
      dispatch(clearFormErrors());
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!initialFormData) {
      showToast('Error', 'Initial data not loaded', 'error');
      return;
    }

    // Remove course object from payload (it's only for display)
    const { course: _, ...submitData } = formData;

    dispatch(updateLesson({
      token: user.token,
      lessonId: id,
      formData: submitData,
      initialData: initialFormData,
    }));
  };

  useEffect(() => {
    if (updateStatus === 'succeeded') {
      showToast('Success', 'Lesson updated successfully', 'success');
      dispatch(resetSubmissionStatus());
      navigate(`/dashboard/courses/lesson/${courseId || selectedLesson?.course_id}`, { replace: true, state: { course } });
    }
  }, [updateStatus, dispatch, navigate, courseId, selectedLesson, course]);

  useEffect(() => {
    if (updateStatus === 'failed' && updateError) {
      showToast('Error', updateError, 'error');
    }
  }, [updateStatus, updateError]);

  useEffect(() => {
    if (detailStatus === 'failed' && detailError) {
      showToast('Error', detailError, 'error');
      navigate(`/dashboard/courses/lesson/${courseId}`, { replace: true, state: { course } });
    }
  }, [detailStatus, detailError, navigate, courseId, course]);

  const loading = detailStatus === 'loading' || updateStatus === 'loading';

  if (detailStatus === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (detailStatus === 'failed') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{detailError}</p>
          <button
            onClick={() => navigate(`/dashboard/courses/lesson/${courseId}`, { state: { course } })}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Lessons
          </button>
        </div>
      </div>
    );
  }

  if (!selectedLesson) {
    return null;
  }

  return (
    <div className="min-h-screen py-4 px-4 sm:px-6 lg:px-8">
      <div className="max-w-full mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                Edit Lesson
              </h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Update lesson information below
              </p>
            </div>
            <button
              onClick={() => navigate(`/dashboard/courses/lesson/${courseId || selectedLesson?.course_id}`, { state: { course } })}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
          </div>
        </motion.div>

        {(updateError || Object.values(formErrors).some(Boolean)) && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg dark:bg-red-900/20 dark:border-red-800"
          >
            {updateError && (
              <p className="text-red-600 dark:text-red-400 text-sm font-medium mb-2">
                {updateError}
              </p>
            )}
            {Object.entries(formErrors).map(([field, errorMsg]) => (
              errorMsg && (
                <p key={field} className="text-red-600 dark:text-red-400 text-sm">
                  {field}: {errorMsg}
                </p>
              )
            ))}
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 lg:p-8 relative" style={{ overflow: 'visible' }}
        >
          <SharedForm
            fields={lessonFields}
            formData={formData}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            loading={loading}
            entity="lesson"
            formErrors={formErrors}
          />
        </motion.div>
      </div>
    </div>
  );
};

export default EditLessonPage;
